import React, { useState } from "react";
import {
    Typography,
    Grid,
    FormControl,
    Select,
    TextField,
    MenuItem,
    FormControlLabel,
    Stack,
    Radio,
    RadioGroup,
    IconButton
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addMonths, addYears, format } from "date-fns";
import { useAtom } from "jotai";
import {
    RECURRING_PATTERN_INDEF,
    RECURRING_PATTERN_UNTIL,
    RECURRING_PATTERN_TOTAL,
    recurringSettingsAtom,
    setRecurringDetailAtom,
    days
} from "../atoms/recurringSettings";
import { bookingDetailsAtom, customTimeActionsAtom } from "../atoms/booking";
import {
    FREQ_MONTHLY,
    FREQ_YEARLY,
    FREQ_WEEKLY
} from "../modules/RecurringBookingsUtilityFunctions";
import { getWeekOfMonth, getDay } from "date-fns";
import { dayNames, weekOfMonth } from "../utils/Constants";
import { getOrdinalSuffix } from "../utils";
import {
    selectedRescheduleDateTimeAtom,
    calendarDateAtom
} from "../atoms/providerReschedule";
import moment from "moment";
export const RepeatingSetup = ({
    toggleCustomDateTimeDialogVisibility = () => {}
}) => {
    const [, setDetail] = useAtom(setRecurringDetailAtom);
    const [
        {
            interval,
            frequency,
            recurringPattern,
            totalSessionsNumber,
            cronologicalOrCalendarDay,
            untilDate,
            recurringWeekDays
        }
    ] = useAtom(recurringSettingsAtom);
    const [{ sessions }] = useAtom(bookingDetailsAtom);
    const [selectedRescheduleDateTime] = useAtom(
        selectedRescheduleDateTimeAtom
    );
    const [reschdeuleCalendateDate] = useAtom(calendarDateAtom);
    const [openUntilDate, setOpenUntilDate] = useState(false);
    const dayStyles = {
        width: 36,
        height: 36,
        fontSize: "14px",
        borderRadius: "50%",
        borderStyle: "solid",
        borderWidth: "1px",
        minWidth: 0,
        "&:hover": {
            backgroundColor: "primary.main",
            borderColor: "primary.main",
            color: "white"
        }
    };

    const selectedDayStyle = (selected) => ({
        backgroundColor: selected ? "primary.main" : "white",
        borderColor: selected ? "primary.main" : "rgb(0,0,0,0.23)",
        color: selected ? "white" : "primary.main"
    });

    function considerDate() {
        if (reschdeuleCalendateDate) return reschdeuleCalendateDate;
        if (selectedRescheduleDateTime) return selectedRescheduleDateTime;
        if (sessions && sessions.length) return sessions[0].dateTime;
    }

    const handleUntilDateChange = (newDate) => {
        setDetail({
            untilDate: newDate
        });
    };

    return (
        (reschdeuleCalendateDate ||
            selectedRescheduleDateTime ||
            sessions?.length) && (
            <>
                <Grid container mb={2}>
                    <Stack direction="row" spacing={2}>
                        <Typography>Every</Typography>
                        <FormControl>
                            <Select
                                id="number-selector"
                                value={interval}
                                onChange={({ target: { value: interval } }) => {
                                    setDetail({ interval });
                                }}
                            >
                                {Array.from({ length: 10 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Select
                            sx={{
                                minWidth: "85px"
                            }}
                            value={frequency}
                            onChange={({ target: { value: frequency } }) =>
                                setDetail({ frequency })
                            }
                        >
                            {[
                                {
                                    val: FREQ_WEEKLY,
                                    label: interval > 1 ? "Weeks" : "Week"
                                },
                                {
                                    val: FREQ_MONTHLY,
                                    label: interval > 1 ? "Months" : "Month"
                                },
                                {
                                    val: FREQ_YEARLY,
                                    label: interval > 1 ? "Years" : "Year"
                                }
                            ].map(({ val, label }) => (
                                <MenuItem value={val} key={val}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                        {frequency === FREQ_MONTHLY && (
                            <>
                                <Typography>on</Typography>
                                <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    defaultValue="nth-day"
                                    name="radio-buttons-group"
                                    value={cronologicalOrCalendarDay}
                                    sx={{ gap: "8px" }}
                                    onChange={({
                                        target: {
                                            value: cronologicalOrCalendarDay
                                        }
                                    }) =>
                                        setDetail({
                                            cronologicalOrCalendarDay
                                        })
                                    }
                                >
                                    <FormControlLabel
                                        key={"nth-day"}
                                        value={"CRONO"}
                                        control={<Radio disableRipple />}
                                        label={`Day ${format(considerDate(), "d")}`}
                                    />
                                    <FormControlLabel
                                        key={"nth-week-day"}
                                        value={"CALENDAR"}
                                        control={<Radio disableRipple />}
                                        label={`Every ${weekOfMonth[getWeekOfMonth(considerDate()) - 1]}
                                         ${dayNames[getDay(considerDate())]}`}
                                    />
                                </RadioGroup>
                            </>
                        )}
                        {frequency === FREQ_YEARLY && (
                            <Typography>
                                on {format(considerDate(), "MMMM d")}
                                {getOrdinalSuffix(format(considerDate(), "d"))}
                            </Typography>
                        )}
                        {frequency === FREQ_WEEKLY && (
                            <Grid
                                sx={{
                                    display: "flex",
                                    gap: "8px",
                                    marginTop: "12px"
                                }}
                                item
                                xs={12}
                                lg={12}
                            >
                                {days.map((day) => (
                                    <IconButton
                                        key={day.id}
                                        onClick={() => {
                                            if (
                                                day.id !==
                                                moment(considerDate()).day()
                                            ) {
                                                setDetail({
                                                    recurringWeekDays:
                                                        recurringWeekDays.includes(
                                                            day.id
                                                        )
                                                            ? recurringWeekDays.filter(
                                                                  (
                                                                      selectedDay
                                                                  ) =>
                                                                      selectedDay !==
                                                                      day.id
                                                              )
                                                            : [
                                                                  ...recurringWeekDays,
                                                                  day.id
                                                              ]
                                                });
                                            }
                                        }}
                                        sx={{
                                            ...dayStyles,
                                            ...selectedDayStyle(
                                                recurringWeekDays.includes(
                                                    day.id
                                                )
                                            )
                                        }}
                                    >
                                        <span>{day.title}</span>
                                    </IconButton>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                </Grid>
                <Grid container>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="nth-day"
                        name="radio-buttons-group"
                        value={recurringPattern}
                        onChange={({ target: { value: recurringPattern } }) =>
                            setDetail({ recurringPattern })
                        }
                    >
                        <Stack direction="row" spacing={2}>
                            <FormControlLabel
                                key={RECURRING_PATTERN_INDEF}
                                value={RECURRING_PATTERN_INDEF}
                                control={<Radio disableRipple />}
                                label="Indefinitely"
                            />
                            <FormControlLabel
                                key={RECURRING_PATTERN_UNTIL}
                                value={RECURRING_PATTERN_UNTIL}
                                control={
                                    <Radio
                                        disableRipple
                                        onChange={
                                            (({
                                                target: {
                                                    value: recurringPattern
                                                }
                                            }) =>
                                                setDetail({ recurringPattern }),
                                            () => setOpenUntilDate(true))
                                        }
                                    />
                                }
                                label={"Until"}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    id="end-date"
                                    name="endDate"
                                    size="small"
                                    value={
                                        untilDate
                                            ? untilDate
                                            : frequency === FREQ_YEARLY
                                              ? addYears(new Date(), 5)
                                              : addMonths(new Date(), 6)
                                    }
                                    fullWidth
                                    inputFormat="MMM dd, yyyy"
                                    disablePast
                                    onChange={(e) => handleUntilDateChange(e)}
                                    open={openUntilDate}
                                    onOpen={() => setOpenUntilDate(true)}
                                    onClose={() => setOpenUntilDate(false)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            disabled={
                                                recurringPattern !==
                                                RECURRING_PATTERN_UNTIL
                                            }
                                            error={false}
                                            inputProps={{
                                                ...params.inputProps,
                                                readOnly: true
                                            }}
                                            onClick={(e) =>
                                                recurringPattern ===
                                                    RECURRING_PATTERN_UNTIL &&
                                                setOpenUntilDate(true)
                                            }
                                            sx={{
                                                "& .MuiInputAdornment-root": {
                                                    display: "none"
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>

                            <FormControlLabel
                                key={RECURRING_PATTERN_TOTAL}
                                value={RECURRING_PATTERN_TOTAL}
                                control={<Radio disableRipple />}
                                label="For a total of"
                            />
                            <Select
                                value={totalSessionsNumber}
                                sx={{
                                    minWidth: "67px"
                                }}
                                onChange={({
                                    target: { value: totalSessionsNumber }
                                }) => setDetail({ totalSessionsNumber })}
                                disabled={
                                    recurringPattern !== RECURRING_PATTERN_TOTAL
                                }
                            >
                                {Array.from({ length: 99 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography>sessions</Typography>
                        </Stack>
                    </RadioGroup>
                </Grid>
            </>
        )
    );
};
